import React from "react";
import {
  Popover,
  Box,
  Grid,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Avatar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import LogoutIcon from "@mui/icons-material/Logout";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";

interface ProfilePopoverProps {
  id?: string;
  open: boolean;
  anchorEl: any;
  onClose: () => void;
  userContext: any;
  organizationList: any[];
  branchList: any[];
  onOrganizationClick: (item: any) => void;
  onBranchClick: (item: any) => void;
  onProfileClick: () => void;
  onSignOut: () => void;
  onLocationClick?: () => void;
  stringAvatar: (name: string) => any;
  stringAvatarOrganization: (name: string) => any;
}

export const ProfilePopover: React.FC<ProfilePopoverProps> = React.memo(
  ({
    id,
    open,
    anchorEl,
    onClose,
    userContext,
    organizationList,
    branchList,
    onOrganizationClick,
    onBranchClick,
    onProfileClick,
    onSignOut,
    onLocationClick,
    stringAvatar,
    stringAvatarOrganization,
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const canChangeLocation =
      isMobile &&
      userContext?.locationCount > 1 &&
      userContext?.loginDetail?.userRole !== "user";

    return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      <Box className="main-profie-dialog">
        <Grid className="main-profie-container">
          <Grid className="profile-user-detail-component">
            <Grid className="profile-component-grid">
              <Grid width="100%" paddingX="8px">
                <Grid
                  className="organization-container"
                  onClick={() => {
                    if (canChangeLocation && onLocationClick) {
                      onLocationClick();
                    }
                  }}
                  sx={{
                    backgroundColor: "#f1f5f8 !important",
                    cursor: canChangeLocation ? "pointer" : "default",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    "&:hover": canChangeLocation
                      ? { backgroundColor: "#e8eef5 !important" }
                      : undefined,
                  }}
                >
                  <Grid
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <Grid color="#0b0b45 !important" sx={{ paddingTop: "2px" }}>
                      <BusinessIcon />
                    </Grid>
                    <Grid marginTop={0.5} marginLeft={1.3} sx={{ minWidth: 0 }}>
                      <h5 className="profilename" style={{ margin: 0 }}>
                        {userContext.locationName ||
                          userContext.organizationName}{" "}
                        {" ("}
                        {userContext.locationCity || userContext.branchName}
                        {")"}
                      </h5>
                      {canChangeLocation && (
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "#285cdb",
                            fontWeight: 600,
                            mt: 0.25,
                          }}
                        >
                          Tap to change location
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                  {canChangeLocation && (
                    <SwapVertIcon
                      sx={{
                        color: "#285cdb",
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid className="avatar-upload">
                <Grid className="avatar-edit"></Grid>
                <Grid className="avatar-preview">
                  <Typography
                    className="avtar"
                    {...stringAvatar(
                      `${userContext.loginDetail.firstName} ${userContext.loginDetail.lastName}`,
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid className="profile-name-grid">
              <h4 className="text-size-medium">
                {userContext.loginDetail.fullName}
              </h4>
              <Grid style={{ marginTop: "-5px" }}>
                <Typography className="text-size-small">
                  {userContext.loginDetail.email}
                </Typography>
              </Grid>
              <Grid className="grid-myprofile">
                <Button
                  className="one-myprofile"
                  color="primary"
                  variant="outlined"
                  size="medium"
                  onClick={onProfileClick}
                >
                  My Profile
                </Button>
              </Grid>
            </Grid>
          </Grid>
          {branchList.length > 1 &&
            userContext.loginDetail.userRole !== "user" && (
              <Grid className="branch-selection-component">
                <PopupState variant="popover" popupId="demo-popup-popover">
                  {(popupState) => (
                    <Box>
                      <Button
                        className="branch-selection-btn"
                        {...bindTrigger(popupState)}
                        variant="outlined"
                        startIcon={<BusinessIcon />}
                      >
                        <Typography className="branch-selection">
                          {userContext.branchName}
                        </Typography>
                      </Button>
                      <Popover
                        {...bindPopover(popupState)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "center",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "center",
                        }}
                        className="nav-popover-branch"
                      >
                        <Box
                          className="branch-container"
                          sx={{
                            "& > :not(style)": {
                              maxwidth: "20px",
                            },
                          }}
                        >
                          <List>
                            {branchList.map((item: any, index) => {
                              return (
                                <ListItem
                                  disablePadding
                                  button
                                  onClick={() => {
                                    popupState.close();
                                    onBranchClick(item);
                                  }}
                                  key={index}
                                >
                                  <ListItemText primary={item.branchName} />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Box>
                      </Popover>
                    </Box>
                  )}
                </PopupState>
              </Grid>
            )}
          {(userContext.loginDetail.userRole === "superadmin" ||
            userContext.loginDetail.userRole === "clientadmin") &&
            organizationList.filter(
              (item: any) => userContext.organizationId !== item.organizationId,
            ).length > 0 && (
              <Grid className="organization-component">
                <List sx={{ padding: "0px !important" }}>
                  {organizationList.map(
                    (item: any, index) =>
                      userContext.organizationId !== item.organizationId && (
                        <Grid
                          item
                          className="organization-list-data"
                          key={index}
                        >
                          <ListItem
                            disablePadding
                            button
                            onClick={() => onOrganizationClick(item)}
                          >
                            <span className="organization-list-icon">
                              <Avatar
                                {...stringAvatarOrganization(
                                  item.organizationName,
                                )}
                                style={{ paddingTop: "4px" }}
                              />
                            </span>
                            <ListItemText
                              primary={
                                <h4 style={{ fontSize: "14px" }}>
                                  {item.organizationName}
                                </h4>
                              }
                            />
                          </ListItem>
                        </Grid>
                      ),
                  )}
                </List>
              </Grid>
            )}

          <Grid className="sign-out-grid">
            <Grid>
              <Button
                className="sign-out-btn-myprofile"
                color="primary"
                variant="contained"
                size="medium"
                onClick={onSignOut}
                startIcon={<LogoutIcon />}
              >
                Sign out
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Popover>
    );
  },
);

ProfilePopover.displayName = "ProfilePopover";
